import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { publicApi } from '../lib/api';
import { resolveImageUrl } from '../lib/utils';
import PageSEO from '../components/PageSEO';

export const HomePage = () => {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await publicApi.getProjects();
        setProjects(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };
    fetchProjects();
  }, []);

  const services = [
    { title: "Kentsel Dönüşüm", desc: "Riskli yapıların güvenli yaşam alanlarına dönüşümü" },
    { title: "İnşaat ve Taahhüt", desc: "Anahtar teslim projeler ve profesyonel uygulama" },
    { title: "Proje Geliştirme", desc: "Arsa değerlendirmeden teslimata kapsamlı yönetim" },
    { title: "Mimari Tasarım", desc: "Fonksiyonel ve estetik yaşam alanları kurgusu" },
  ];

  return (
    <div data-testid="home-page" className="page-transition">
      <PageSEO
        title=""
        description="VE PERA İnşaat — Kentsel dönüşüm, inşaat, proje geliştirme ve mimari tasarım alanlarında güvenli yapılar ve değerli yaşam alanları üretiyoruz. İstanbul merkezli premium inşaat firması."
        path="/"
      />
      {/* Hero Section - Full viewport, cinematic */}
      <section
        data-testid="hero-section"
        className="relative h-screen flex items-end overflow-hidden"
      >
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1763065046337-e8848f3c186f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxhcmNoaXRlY3R1cmFsJTIwYnVpbGRpbmclMjBmYWNhZGUlMjBtaW5pbWFsaXN0JTIwY29uY3JldGUlMjBnbGFzc3xlbnwwfHx8fDE3NzU0NjUwMTB8MA&ixlib=rb-4.1.0&q=85"
            alt="Premium architecture"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0"></div>
        </div>

        {/* Content - Bottom aligned, editorial layout */}
        <div className="relative z-10 w-full pb-16 lg:pb-24">
          <div className="container-wide">
            <div className="grid lg:grid-cols-12 gap-8 items-end">
              {/* Main heading */}
              <div className="lg:col-span-8">
                <h1
                  className="text-[clamp(2.5rem,8vw,5.5rem)] leading-[0.95] tracking-[-0.03em] text-white mb-8"
                  style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
                >
                  Geleceğin yaşam
                  <br />
                  alanlarını <span className="italic font-light text-[#d1d1d6]">bugünden</span>
                  <br />
                  inşa ediyoruz.
                </h1>
              </div>
              
              {/* Side content */}
              <div className="lg:col-span-4 lg:text-right">
                <p 
                  className="text-[15px] text-[#d1d1d6]/80 leading-[1.8] mb-8 max-w-sm lg:ml-auto"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  Güvenli yapılar. Değerli yaşam alanları. Şehirlerin siluetini yeniden şekillendiren vizyoner projeler.
                </p>
                <div className="flex flex-col sm:flex-row lg:flex-col gap-4 lg:items-end">
                  <Link
                    to="/projeler"
                    data-testid="hero-cta-projects"
                    className="btn-hero-primary"
                  >
                    Projeleri Keşfet
                    <ArrowRight size={14} />
                  </Link>
                  <Link
                    to="/iletisim"
                    data-testid="hero-cta-contact"
                    className="btn-hero-outline"
                  >
                    İletişime Geç
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:block">
          <div className="flex flex-col items-center gap-3 text-white/40">
            <span 
              className="text-[10px] tracking-[0.2em] uppercase"
              style={{ fontFamily: "var(--font-body)" }}
            >
              Keşfet
            </span>
            <div className="w-[1px] h-12 bg-white/30"></div>
          </div>
        </div>
      </section>

      {/* Vision Section - Editorial asymmetric layout */}
      <section className="section-padding bg-[#f2f2f7]">
        <div className="container-wide">
          <div className="grid lg:grid-cols-12 gap-16 lg:gap-8">
            {/* Left - Large image */}
            <div className="lg:col-span-6 lg:col-start-1">
              <div className="image-reveal aspect-[4/5] lg:aspect-auto lg:h-[700px]">
                <img
                  src="https://images.unsplash.com/photo-1740481916141-eebde9ce8e8e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjByZXNpZGVudGlhbCUyMGJ1aWxkaW5nJTIwZXh0ZXJpb3IlMjBJc3RhbmJ1bHxlbnwwfHx8fDE3NzU0NjUwMDl8MA&ixlib=rb-4.1.0&q=85"
                  alt="Modern yaşam alanı"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            {/* Right - Text content */}
            <div className="lg:col-span-5 lg:col-start-8 flex flex-col justify-center">
              <span className="overline mb-6">Vizyonumuz</span>
              <h2
                className="text-[clamp(2rem,4vw,3rem)] leading-[1.1] tracking-[-0.02em] mb-8"
                style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
              >
                Mimari mükemmellik,
                <br />kalıcı değer
              </h2>
              <div className="divider mb-8"></div>
              <p 
                className="text-[#8e8e93] leading-[1.9] mb-6 text-[15px]"
                style={{ fontFamily: "var(--font-body)" }}
              >
                Ve Pera İnşaat, yapı sektöründe mühendislik mükemmelliğini, estetik tasarımı ve uzun vadeli değer yaratmayı bir araya getiren vizyoner bir marka.
              </p>
              <p 
                className="text-[#8e8e93] leading-[1.9] mb-10 text-[15px]"
                style={{ fontFamily: "var(--font-body)" }}
              >
                Her projemizde güvenli, değerli ve estetik yaşam alanları oluşturuyoruz. Şehirlerin siluetine kalıcı izler bırakıyoruz.
              </p>
              <Link 
                to="/hakkimizda" 
                className="link-line text-[#1a1a1a] hover:text-[#041f60] transition-colors"
              >
                Hikayemizi Keşfedin
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Minimal grid */}
      <section className="section-padding bg-[#1a1a1a] text-white">
        <div className="container-wide">
          <div className="grid lg:grid-cols-12 gap-16 mb-20">
            <div className="lg:col-span-5">
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#f2f2f7]/70 mb-6 block" style={{ fontFamily: "var(--font-body)", fontWeight: 500 }}>Hizmetlerimiz</span>
              <h2
                className="text-[clamp(2rem,4vw,3rem)] leading-[1.1] tracking-[-0.02em] text-[#f2f2f7]"
                style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
              >
                Uzmanlık<br />alanlarımız
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
              <p 
                className="text-[#d1d1d6]/70 leading-[1.9] text-[15px]"
                style={{ fontFamily: "var(--font-body)" }}
              >
                İnşaat ve proje geliştirme alanında kapsamlı hizmetler. Her projede mühendislik mükemmelliği ve değer yaratma hedefi.
              </p>
            </div>
          </div>
          
          {/* Services grid - clean lines */}
          <div className="border-t border-[#8e8e93]/30">
            {services.map((service, index) => (
              <Link
                key={index}
                to="/hizmetler"
                className="group flex items-center justify-between gap-6 py-8 border-b border-[#8e8e93]/30 hover:bg-[#f2f2f7]/5 transition-colors px-4 -mx-4"
              >
                <div className="flex-1">
                  <h3 
                    className="text-[20px] lg:text-[24px] text-[#f2f2f7] group-hover:text-[#b87333] transition-colors"
                    style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
                  >
                    {service.title}
                  </h3>
                </div>
                <div className="flex-1 hidden lg:block">
                  <p 
                    className="text-[#d1d1d6]/60 text-[15px]"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {service.desc}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <ArrowUpRight className="w-5 h-5 text-[#8e8e93] group-hover:text-[#b87333] transition-colors" />
                </div>
              </Link>
            ))}
          </div>
          
          <div className="mt-12">
            <Link to="/hizmetler" className="link-line text-[#d1d1d6] hover:text-[#b87333] transition-colors">
              Tüm Hizmetleri Gör
            </Link>
          </div>
        </div>
      </section>

      {/* Projects Section - Editorial grid */}
      <section className="section-padding bg-[#f2f2f7]">
        <div className="container-wide">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <div>
              <span className="overline mb-6 block">Projelerimiz</span>
              <h2
                className="text-[clamp(2rem,4vw,3rem)] leading-[1.1] tracking-[-0.02em]"
                style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
              >
                Değer yaratan projeler
              </h2>
            </div>
            <Link to="/projeler" className="link-line text-[#1a1a1a] hover:text-[#041f60] transition-colors">
              Tüm Projeleri Gör
            </Link>
          </div>

          {/* Projects - Asymmetric layout */}
          <div className="grid lg:grid-cols-12 gap-8">
            {projects.map((project, index) => (
              <div
                key={project.id}
                data-testid={`project-card-${index}`}
                className={`group ${
                  index === 0 ? 'lg:col-span-7' : 'lg:col-span-5'
                }`}
              >
                <div className={`image-reveal ${index === 0 ? 'aspect-[4/3]' : 'aspect-[4/3]'} mb-6 relative`}>
                  <img
                    src={resolveImageUrl(project.image_url)}
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                  <span
                    className="absolute bottom-0 left-0 right-0 py-2.5 px-4 text-[10px] tracking-[0.08em] text-white/60 bg-gradient-to-t from-black/40 to-transparent"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    * Görsel temsilidir. Gerçek proje görselleriyle güncellenecektir.
                  </span>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span 
                      className="text-[11px] tracking-[0.1em] uppercase text-[#8e8e93] mb-2 block"
                      style={{ fontFamily: "var(--font-body)" }}
                    >
                      {project.location}
                    </span>
                    <h3
                      className="text-[20px] lg:text-[24px] group-hover:text-[#b87333] transition-colors"
                      style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
                    >
                      {project.title}
                    </h3>
                  </div>
                  <span className={`badge-status badge-${project.status.replace('_', '-')}`}>
                    {project.status === 'completed' ? 'Tamamlandı' : 
                     project.status === 'in_progress' ? 'Devam Ediyor' : 'Planlanan'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marka Yaklaşımı */}
      <section className="py-24 lg:py-32 bg-[#f2f2f7]">
        <div className="container-wide">
          <div className="mb-16 lg:mb-20">
            <span className="overline mb-5 block">Yaklaşımımız</span>
            <h2
              className="text-[clamp(1.75rem,3.5vw,2.5rem)] leading-[1.15] tracking-[-0.02em] max-w-xl"
              style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
            >
              Yeni bir vizyon,<br />
              <span className="italic font-light text-[#8e8e93]">köklü bir mühendislik anlayışı</span>
            </h2>
          </div>
          <div className="border-t border-[#d1d1d6]">
            {[
              {
                title: "Güçlü Mühendislik Yaklaşımı",
                desc: "Her projemizde yapısal güvenliği ve mühendislik doğruluğunu ön planda tutuyoruz. Teknik mükemmellik, işimizin temelidir."
              },
              {
                title: "Titizlikle Planlanan Süreçler",
                desc: "Şeffaf, kontrollü ve öngörülebilir süreç yönetimi ile her aşamayı özenle planlıyor, her detayı takip ediyoruz."
              },
              {
                title: "Değer Odaklı Yaşam Alanları",
                desc: "Sadece yapı üretmiyoruz — nitelikli, sürdürülebilir ve uzun vadeli değer yaratan yaşam alanları tasarlıyoruz."
              },
            ].map((item, index) => (
              <div
                key={index}
                className="grid grid-cols-12 gap-4 lg:gap-8 py-10 lg:py-12 border-b border-[#d1d1d6] group"
              >
                <div className="col-span-12 lg:col-span-4">
                  <h3
                    className="text-[18px] lg:text-[22px] group-hover:text-[#041f60] transition-colors duration-500"
                    style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
                  >
                    {item.title}
                  </h3>
                </div>
                <div className="col-span-12 lg:col-span-6 lg:col-start-6">
                  <p
                    className="text-[#8e8e93] text-[14px] lg:text-[15px] leading-[1.9]"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Full width image */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1606589689701-f83ea123d523?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwY29uc3RydWN0aW9uJTIwc2l0ZSUyMGFlcmlhbCUyMHZpZXclMjBtb2Rlcm58ZW58MHx8fHwxNzc1NDY1MDA5fDA&ixlib=rb-4.1.0&q=85"
            alt="Architecture"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1a1a1a]/60"></div>
        </div>
        <div className="relative z-10 container-wide">
          <div className="max-w-2xl">
            <h2
              className="text-[clamp(2rem,5vw,3.5rem)] leading-[1.1] tracking-[-0.02em] text-white mb-8"
              style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
            >
              Projenizi birlikte
              <br />hayata geçirelim
            </h2>
            <p 
              className="text-[#d1d1d6]/80 leading-[1.8] mb-10 text-[15px] max-w-lg"
              style={{ fontFamily: "var(--font-body)" }}
            >
              Size özel çözümlerimizi keşfedin. Uzman ekibimiz sorularınızı yanıtlamak için hazır.
            </p>
            <Link
              to="/iletisim"
              data-testid="final-cta"
              className="btn-hero-primary"
            >
              İletişime Geçin
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
